import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'

import { TranslateModule } from '@ngx-translate/core'
import { BehaviorSubject, filter, takeUntil, tap, withLatestFrom } from 'rxjs'

import { DestroyDirective } from '~/core/directives'
import {
  DEFAULT_MKT_CAP_ORDER,
  DEFAULT_MKT_CAP_SORT_BY,
  PerformanceFacade,
  UiFacade,
} from '~/core/features'
import { EPerformanceSummaryFlag, IPerformanceMktCap } from '~/core/models'
import { AsyncBlockComponent } from '~/ui/async-block'
import { PerformanceWinTableComponent } from '~/ui/performance-win-table'

import { MARKET_CUB_COLUMNS } from './market-cup-tab.constant'

@Component({
  selector: 'app-market-cup-tab',
  templateUrl: './market-cup-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [DestroyDirective],
  imports: [AsyncPipe, TranslateModule, AsyncBlockComponent, PerformanceWinTableComponent],
})
export class MarketCupTabComponent implements OnInit {
  private readonly destroy$ = inject(DestroyDirective).destroy$

  readonly uiFacade = inject(UiFacade)
  readonly performanceFacade = inject(PerformanceFacade)

  MARKET_CUB_COLUMNS = MARKET_CUB_COLUMNS
  DEFAULT_MKT_CAP_SORT_BY = DEFAULT_MKT_CAP_SORT_BY
  DEFAULT_MKT_CAP_ORDER = DEFAULT_MKT_CAP_ORDER

  readonly data$ = new BehaviorSubject<IPerformanceMktCap[]>([])
  // TODO: Find a common way. total, results
  readonly total$ = new BehaviorSubject<IPerformanceMktCap | null>(null)
  readonly results$ = new BehaviorSubject<IPerformanceMktCap | null>(null)

  constructor() {}

  ngOnInit(): void {
    this.performanceFacade.quarter$
      .pipe(
        filter(quarter => Boolean(quarter)),
        withLatestFrom(this.performanceFacade.mktCapSortBy$, this.performanceFacade.mktCapOrder$),
        tap(([, sortBy, order]) =>
          this.performanceFacade.getPerformanceMktCap(
            sortBy || DEFAULT_MKT_CAP_SORT_BY,
            order || DEFAULT_MKT_CAP_ORDER
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe()

    this.performanceFacade.mktCapData$
      .pipe(
        tap(data => {
          const performanceData: IPerformanceMktCap[] = []

          // TODO: Find a common way. total, results
          for (const performance of data || []) {
            switch (performance.summaryFlag) {
              case EPerformanceSummaryFlag.SUMMARY_COLUMN:
                this.total$.next(performance)
                break

              case EPerformanceSummaryFlag.RESULTS_COLUMN:
                this.results$.next(performance)
                break

              default:
                performanceData.push(performance)
                break
            }
          }

          this.data$.next(performanceData)
        }),
        takeUntil(this.destroy$)
      )
      .subscribe()
  }

  changeSort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.performanceFacade.getPerformanceMktCap(sortBy, order)
  }
}
